import { BaccaratCardPosition, BaccaratOutcome, BaccaratSidePosition } from './baccarat.model';
import { TableUpdate } from '../../../models/communication/app-state.model';
import { CardPosition, Outcome, SidePosition } from '../../../models/communication/games.model';
import { GameBase } from '../../game-base';
import { CardState, SideState } from '../../models/game-view.model';

export class BaccaratGame extends GameBase {
    constructor() {
        super({
            sides: [
                {
                    position: BaccaratSidePosition.Banker,
                    hands: [
                        {
                            label: $localize`BANKER`,
                            handValueKey: 'bankerHandValue',
                            cards: [
                                {
                                    position: BaccaratCardPosition.BankerThirdCard,
                                    state: CardState.Hidden,
                                    isRotated: true
                                },
                                { position: BaccaratCardPosition.BankerSecondCard },
                                { position: BaccaratCardPosition.BankerFirstCard }
                            ]
                        }
                    ]
                },
                {
                    position: BaccaratSidePosition.Player,
                    hands: [
                        {
                            label: $localize`PLAYER`,
                            handValueKey: 'playerHandValue',
                            cards: [
                                { position: BaccaratCardPosition.PlayerSecondCard },
                                { position: BaccaratCardPosition.PlayerFirstCard },
                                {
                                    position: BaccaratCardPosition.PlayerThirdCard,
                                    state: CardState.Hidden,
                                    isRotated: true
                                }
                            ]
                        }
                    ]
                }
            ]
        });
    }

    override getFirstCardPosition(): CardPosition {
        return BaccaratCardPosition.PlayerFirstCard;
    }

    override handleOutcome(table: TableUpdate): void {
        super.handleOutcome(table);

        if (table.outcome !== undefined) {
            const sidePosition = this.getSidePositionFromOutcome(table.outcome);
            if (sidePosition !== undefined) {
                const hand = this.getFirstHandInSide(sidePosition);
                this.updateTitle(`${this.getOutcomeLabel(table.outcome)} - ${hand.amount.value}`);
            }
        }
    }

    getSidePositionFromOutcome(outcome: Outcome): SidePosition | undefined {
        switch (outcome) {
            case BaccaratOutcome.BankerWin:
                return BaccaratSidePosition.Banker;
            case BaccaratOutcome.PlayerWin:
                return BaccaratSidePosition.Player;
            default:
                return undefined;
        }
    }

    override getFieldLabel(cardPosition: CardPosition): string {
        switch (cardPosition as BaccaratCardPosition) {
            case BaccaratCardPosition.PlayerFirstCard:
            case BaccaratCardPosition.PlayerSecondCard:
            case BaccaratCardPosition.PlayerThirdCard:
                return $localize`PLAYER FIELD`;
            case BaccaratCardPosition.BankerFirstCard:
            case BaccaratCardPosition.BankerSecondCard:
            case BaccaratCardPosition.BankerThirdCard:
                return $localize`BANKER FIELD`;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown card position: ${String(cardPosition)}`);
                return '';
        }
    }

    override getOutcomeLabel(outcome: Outcome): string {
        switch (outcome as BaccaratOutcome) {
            case BaccaratOutcome.PlayerWin:
                return $localize`PLAYER WIN`;
            case BaccaratOutcome.BankerWin:
                return $localize`BANKER WIN`;
            case BaccaratOutcome.Tie:
                return $localize`TIE`;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown outcome: ${String(outcome)}`);
                return '';
        }
    }

    override setOutcomeStates(outcome: Outcome): void {
        switch (outcome as BaccaratOutcome) {
            case BaccaratOutcome.Tie:
                this.updateSides(SideState.Tie);
                break;
            case BaccaratOutcome.PlayerWin:
                this.updateSide(BaccaratSidePosition.Player, SideState.Win);
                break;
            case BaccaratOutcome.BankerWin:
                this.updateSide(BaccaratSidePosition.Banker, SideState.Win);
        }
    }
}
